import {useQuery} from "@apollo/client";
import {
    CentralOrderListResponse,
    GET_ORDER_LISTING_FOR_CENTRAL,
} from "../graphql/queries/getOrderListingForCentral";
import {useEffect, useMemo, useState} from "react";
import {formatDateTimeRangeToISO} from "../../../../utils/dateTimeUtils";
import {OrderType} from "../../../../graphql/types/OrderType";
import {OrderFiltersType} from "../../../../graphql/types/OrderFiltersType";

export interface ParcelQueryResult {
    parcelData: OrderType[];
    loading: boolean;
    refetch: (variables?: any) => Promise<any>;
    totalElements: number;
    accessiblePages: number;
    buildQueryVariables: () => any;
}

export const useCentralParcelQuery = (
    filters: OrderFiltersType,
    currentPage: number,
    pageSize: number
): ParcelQueryResult => {
    const [parcelData, setParcelData] = useState<OrderType[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [accessiblePages, setAccessiblePages] = useState(0);

    const buildQueryVariables = () => {
        // receivedAtHub 功能已移除

        const timeRanges = {
            orderTime: formatDateTimeRangeToISO(filters.orderTime),
            receivedAtHub: formatDateTimeRangeToISO(filters.receivedAtHub),
            destHubArrivedAt: formatDateTimeRangeToISO(filters.destHubArrivedAt),
            destWarehouseArrivedAt: formatDateTimeRangeToISO(filters.destWarehouseArrivedAt),
            deliverAt: formatDateTimeRangeToISO(filters.deliverAt),
            markExceptionAt: formatDateTimeRangeToISO(filters.markExceptionAt),
            deliveredAt: formatDateTimeRangeToISO(filters.deliveredAt),
            lastDeliveryScanTime: formatDateTimeRangeToISO(filters.lastDeliveryScanTime),
            returnArrivalHubAt: formatDateTimeRangeToISO(filters.returnArrivalHubAt)
        };

        // 数字字段兜底：URL hash/表单可能提供 string，这里统一转 number，避免后端内部错误
        const normalizeNumber = (value: any): number | undefined => {
            if (value === undefined || value === null || value === '') return undefined;
            const n = typeof value === 'number' ? value : Number(value);
            return Number.isFinite(n) ? n : undefined;
        };

        // 处理 receiverZipcode：如果有原始输入，转换为数组（与 trackingNumbers 一致）
        let receiverZipcode: string[] | undefined = filters.receiverZipcode;
        if (filters.receiverZipcodeInput) {
            // 支持换行符、半角逗号和空格分隔（多个空格会被视为一个分隔符）
            const zipcodes = filters.receiverZipcodeInput
                .split(/[\r\n,\s]+/)  // 按换行符、逗号或空格分割（\s+ 匹配一个或多个空白字符）
                .map(z => z.trim())
                .filter(z => z.length > 0);
            receiverZipcode = zipcodes.length > 0 ? zipcodes : undefined;
        }
        
        // 构建查询过滤器，避免单值和多值字段冲突
        const queryFilters: any = {
            ...filters,
            ...timeRanges,
            // 转换数组类型为多值查询
            channelIds: filters.channelId?.map(id => parseInt(id)) || undefined,
            pickupHubIds: filters.pickupHubId?.map(id => parseInt(id)) || undefined,
            overallStatuses: filters.overallStatus || undefined,
            nextActions: (filters.nextActions && filters.nextActions.length > 0) ? filters.nextActions : undefined,
            deliveredDspIds: (filters.deliveredDspIds && filters.deliveredDspIds.length > 0) ? filters.deliveredDspIds : undefined,
            receiverZipcode: receiverZipcode, // 使用转换后的数组
            pickupWarehouseId: normalizeNumber((filters as any).pickupWarehouseId),
            cooperationWarehouseId: normalizeNumber((filters as any).cooperationWarehouseId),
            deliveredDspId: normalizeNumber((filters as any).deliveredDspId),
            pickupTaskDate: undefined // 不发送到后端
        };
        
        // 清空临时输入字段，不发送到后端
        queryFilters.receiverZipcodeInput = undefined;
        
        // 清空单值字段，避免冲突
        queryFilters.channelId = undefined;
        queryFilters.pickupHubId = undefined;
        queryFilters.overallStatus = undefined;
        queryFilters.nextAction = undefined;
        queryFilters.deliveredDspId = undefined;

        // 移除无效的时间范围字段，避免GraphQL schema错误
        Object.keys(queryFilters).forEach(key => {
            const value = queryFilters[key];
            if (value === undefined) {
                delete queryFilters[key];
                return;
            }
            if (typeof value === 'object' && value !== null) {
                const hasStart = Object.prototype.hasOwnProperty.call(value, 'start');
                const hasEnd = Object.prototype.hasOwnProperty.call(value, 'end');
                if ((hasStart || hasEnd) && value.start === undefined && value.end === undefined) {
                    delete queryFilters[key];
                }
            }
        });

        return {
            filters: queryFilters,
            pageRequest: {
                page: currentPage,
                size: pageSize,
                sortBy: "orderTime",
                sortDirection: "DESC"
            }
        };
    };

    const memoizedVariables = useMemo(
        () => buildQueryVariables(),
        [filters, currentPage, pageSize]
    );

    // 执行查询
    const { data, loading: queryLoading, refetch } = useQuery<CentralOrderListResponse>(GET_ORDER_LISTING_FOR_CENTRAL,
        {variables: memoizedVariables});

    // 当数据变化时更新状态
    useEffect(() => {
        const responseData = data?.getOrderListingForPickupParcels?.content;
        if (responseData) {
            setParcelData(responseData);
            setLoading(false);
        }
        const totalElements = data?.getOrderListingForPickupParcels?.totalElements;
        if (totalElements) {
            setTotal(totalElements);
        }
        const respAccessiblePages = data?.getOrderListingForPickupParcels?.accessiblePages;
        if (respAccessiblePages !== undefined) {
            setAccessiblePages(respAccessiblePages);
        }
    }, [data]);

    return {
        parcelData,
        loading: loading || queryLoading,
        refetch,
        totalElements: total,
        accessiblePages,
        buildQueryVariables
    };
};